import { Dictionary, debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { IAsyncService } from './context/IAsyncService';
import {
  IAsyncServiceInputConfig,
  useAsyncServiceFetch,
} from './useAsyncServiceFetch';
import { useAsyncServiceVaribles } from './useAsyncServiceVariables';

/**
 * Hook che gestisce la chiamata GET ad un servizio asincrono esterno.
 * Richiama il servizio e si fa restituire i dati in base ai parametri
 * forniti e configurati.
 */
export function useAsyncServiceGetData(config: {
  basePath: string;
  client: ((config: any) => Promise<any>) | undefined | null;
  /** Oggetto che contiene i dati relativi al servizio esterno */
  service: IAsyncService | null | undefined;
  /** Mappa tra i parametri di input della chiamata al servizio e i widget del form */
  inputs: IAsyncServiceInputConfig | null | undefined;
  searchParam?: Record<string, string>;
  disabled?: boolean;
}) {
  const { basePath, client, service, inputs, searchParam, disabled } = config;

  const variables = useAsyncServiceVaribles(basePath, inputs);

  const { handleFetch, data, loading, error, fetchCount } =
    useAsyncServiceFetch({ service, axiosClient: client, inputs, disabled });

  // Indica se la chiamata è stata fatta almeno una volta
  const calledOnce = useRef(false);

  // Gestisco il fetch con un debounce per evitare troppe chiamate.
  // Inoltre passo i searchParams e le variables come parametri per evitare
  // di rigenerare la funzione ogni volta, andando quindi a perdere il debounce
  const debouncedFetch = useMemo(
    () => {
      if (!handleFetch) return null;
      return debounce(async (searchParam: any, variables: Dictionary<any>) => {
        await handleFetch(searchParam, variables);
        // True la chiamata API è stata fatta almeno una volta
        calledOnce.current = fetchCount.current > 0;
      }, 250);
    },
    // Questi parametri dovrebbero rimanere pressoché statici durante l'esecuzione,
    // quindi la funzione non dovrebbe essere rigenerata, mantenendo il debounce
    [fetchCount, handleFetch],
  );

  useEffect(() => {
    debouncedFetch?.(searchParam, variables);
  }, [debouncedFetch, searchParam, variables]);

  return { data, error, loading, calledOnce };
}
